import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Home, Mail, Lock, Eye, EyeOff, ArrowRight, User, Phone, Building } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function RegisterPage() {
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get('role') || "homeowner");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await register({
        ...formData,
        role
      });
      toast.success("Account created successfully!");
      
      if (role === 'contractor') {
        // Redirect contractors to dashboard with trial start parameter
        navigate('/contractor?start_trial=true');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="register-page">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1467733238130-bb6846885316?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmclMjBvbiUyMGZ1c2UlMjBib3glMjByZXNpZGVudGlhbHxlbnwwfHx8fDE3Njg1OTc3NjB8MA&ixlib=rb-4.1.0&q=85"
          alt="Home Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1F2933]/60"></div>
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white text-xl font-medium mb-4">
            "Joining Contractor Hub LLC was the best decision for my business. Quality leads every week!"
          </blockquote>
          <p className="text-white/70">— Mike R., Electrical Contractor</p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-20">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-12">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933]">Contractor Hub LLC</span>
          </Link>

          <h1 className="font-manrope font-bold text-3xl text-[#1F2933] mb-2">
            Create an account
          </h1>
          <p className="text-[#1F2933]/60 mb-8">
            Get started with Contractor Hub LLC today
          </p>

          {/* Role Tabs */}
          <Tabs value={role} onValueChange={setRole} className="mb-6">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="homeowner" data-testid="homeowner-tab">
                <User className="w-4 h-4 mr-2" /> Homeowner
              </TabsTrigger>
              <TabsTrigger value="contractor" data-testid="contractor-tab">
                <Building className="w-4 h-4 mr-2" /> Contractor
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Free Trial Banner for Contractors */}
          {role === "contractor" && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 12v10H4V12"/>
                    <path d="M2 7h20v5H2z"/>
                    <path d="M12 22V7"/>
                    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-900">Start with a Free Trial</h3>
                  <p className="text-emerald-700 text-sm mt-1">
                    No credit card required • 14-day trial • 10 lead credits
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Google Signup */}
          <Button 
            type="button"
            variant="outline"
            className="w-full mb-6 py-6 rounded-xl border-gray-200"
            onClick={googleLogin}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-50 text-[#1F2933]/50">or continue with email</span>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-[#1F2933]">Full Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="pl-12 py-6 rounded-xl"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#1F2933]">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-12 py-6 rounded-xl"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-[#1F2933]">Phone Number</Label>
              <div className="relative mt-2">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(512) 555-0123"
                  className="pl-12 py-6 rounded-xl"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#1F2933]">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  className="pl-12 pr-12 py-6 rounded-xl"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1F2933]/40 hover:text-[#1F2933]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {role === "contractor" && (
              <div className="p-4 bg-[#0FA3B1]/10 rounded-xl">
                <p className="text-sm text-[#1F2933]/80">
                  <strong>Contractor Benefits:</strong> Get listed locally, receive real job requests, and choose which leads you want to pursue—no commissions, no contracts.
                </p>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl btn-shine"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white"></div>
              ) : (
                <>Create Account <ArrowRight className="w-5 h-5 ml-2" /></>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#1F2933]/60">
            Already have an account?{" "}
            <Link to="/login" className="text-[#0FA3B1] font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
